import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import SyncChip, { SyncNotices } from './SyncChip';
import TrialBanner from './billing/TrialBanner';
import ConsentBanner, { ConsentSettingsButton } from './ConsentBanner';
import { useAuth } from '../context/AuthContext';
import { BRAND } from '../domain/brand';
import { resourceMessage } from '../domain/resources';

// Navigation lists the screens that are routed; a nav entry is a promise that
// something is there.
const NAV_GROUPS = [
  {
    label: 'Schedule',
    items: [
      {
        to: '/',
        label: 'Home',
        end: true,
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 7l6-5 6 5v6.5a1 1 0 01-1 1H3a1 1 0 01-1-1V7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            <path d="M6.5 14.5V9.5h3v5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          </svg>
        ),
      },
      {
        to: '/calendar',
        label: 'Calendar',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M1 7h14" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M5 1v4M11 1v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        to: '/students',
        label: 'Students',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M2 14c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        // Online Resources (#424): the library lessons link files from. Beside
        // Students because MMS keeps its teaching sections (Repertoire, Lending
        // Library, Online Resources) together under the roster.
        to: '/resources',
        // A function, because this table is built when the module loads and
        // the core is not loaded until main.jsx has awaited it.
        label: () => resourceMessage('library_title'),
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M1.8 4.2a1 1 0 0 1 1-1h3.3l1.5 1.6h5.6a1 1 0 0 1 1 1v6.9a1 1 0 0 1-1 1H2.8a1 1 0 0 1-1-1V4.2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Money',
    items: [
      {
        to: '/families',
        label: 'Families & Invoices',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="1.5" width="12" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M5 5h6M5 8h4M5 11h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        // Packages → Templates (#181). Under Money rather than Students because
        // a template is priced and a sale is a charge; MMS's own `Packages` page
        // is at studio level, which has no counterpart here.
        to: '/packages',
        label: 'Packages',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 5.5 8 2.5l6 3v5L8 13.5l-6-3v-5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            <path d="M2 5.5 8 8.5m0 0 6-3M8 8.5v5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          </svg>
        ),
      },
      {
        to: '/expenses',
        label: 'Expenses',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2v12M5 5c0-1.657 1.343-3 3-3s3 1.343 3 3c0 3-6 3-6 6 0 1.657 1.343 3 3 3s3-1.343 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
      {
        // MMS's own sidebar entry, beside Expenses & Other Revenue (#476).
        to: '/mileage',
        label: 'Mileage Log',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2.5 13.5a6 6 0 1111 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M8 11l2.5-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Reports',
    items: [
      {
        to: '/reports',
        label: 'Business Reports',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M5 11V7M8 11V5M11 11V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Studio',
    items: [
      {
        to: '/settings',
        label: 'Business Settings',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="2.25" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 1.5l1 1.7 1.9-.5.5 1.9 1.7 1-1 1.7 1 1.7-1.7 1-.5 1.9-1.9-.5-1 1.7-1-1.7-1.9.5-.5-1.9-1.7-1 1-1.7-1-1.7 1.7-1 .5-1.9 1.9.5 1-1.7z"
                  stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
          </svg>
        ),
      },
    ],
  },
];

// Below 1024px the sidebar leaves the row and becomes a drawer behind the
// topbar's menu button (#636), so a phone has the page's width. The breakpoint
// is in app.css, in one @media rule beside the sidebar's own; nothing here
// reads the width, so the button and the drawer are not drawn above it.
export default function AppShell({ children, title, actions }) {
  const { logout } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  const menuButton = useRef(null);
  const sidebar = useRef(null);

  // A link followed closes the drawer: the page it opens is what was wanted.
  useEffect(() => { setNavOpen(false); }, [pathname]);

  // Escape closes it and hands focus back to the button that opened it; opening
  // it moves focus into it, so a keyboard user lands on the links.
  useEffect(() => {
    if (!navOpen) return undefined;
    sidebar.current?.querySelector('a')?.focus();
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setNavOpen(false);
      menuButton.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    <div className="app-shell">
      {navOpen ? <div className="nav-scrim" onClick={() => setNavOpen(false)} /> : null}
      <aside
        id="app-nav"
        ref={sidebar}
        className={`sidebar${navOpen ? ' sidebar-open' : ''}`}
      >
        <div className="sidebar-brand">
          <div className="sidebar-logomark" />
          <span className="sidebar-brand-name">{BRAND.name}</span>
        </div>
        <nav className="sidebar-nav">
          {NAV_GROUPS.map((group) => (
            <div className="nav-group" key={group.label}>
              <div className="nav-group-label">{group.label}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  // "/" is a prefix of every route, so without `end` the Home
                  // link renders active on every page.
                  end={Boolean(item.end)}
                  className={({ isActive }) =>
                    `nav-item${isActive ? ' active' : ''}`
                  }
                >
                  {item.icon}
                  {typeof item.label === 'function' ? item.label() : item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          {/* Both belong to this browser rather than to the studio — see
              ConsentSettingsButton for why neither is on Business Settings. */}
          <ConsentSettingsButton />
          <button
            className="nav-item"
            onClick={logout}
            style={{ marginBottom: '0.75rem' }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="nav-icon">
              <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M10 11l3-3-3-3M13 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Sign out
          </button>
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <button
            ref={menuButton}
            type="button"
            className="topbar-menu"
            aria-label="Menu"
            aria-controls="app-nav"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((o) => !o)}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>
          <h1 className="topbar-title">{title}</h1>
          <div className="topbar-spacer" />
          <SyncChip />
          {actions && <div className="topbar-actions">{actions}</div>}
        </header>
        {/* Under the topbar rather than in it: the topbar is a fixed-height row
            already carrying a title, the sync chip and a page's own actions, and
            a sentence long enough to say what happens when the trial ends would
            push those around on a narrow window. It renders nothing at all
            outside `trial` and `grace` — see TrialBanner. */}
        <TrialBanner />
        {/* #206's list of writes that could not be saved, and #300's note about
            work this device owes another studio, here for TrialBanner's reason:
            the topbar is a fixed-height flex row inside `.main-area { overflow:
            hidden }`, with no room for a list to open. Last of the three,
            because it is often about one write rather than the account, and it
            renders nothing when there is neither a list nor a note. */}
        <SyncNotices />
        {/* Last, because it is about this browser rather than the studio or
            its data, and renders nothing once answered — see ConsentBanner. */}
        <ConsentBanner />
        <main className="page-content">{children}</main>
      </div>
    </div>
  );
}
